import React, { useState } from "react";
import { Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const initial = { name: "", email: "", subject: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("MISSING FIELDS", { description: "Name, email and message are required." });
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("MESSAGE SENT", { description: "We'll get back to you shortly." });
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error("FAILED", { description: String(msg) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 border-b border-[#2A2A2A]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="overline mb-6">// 06 — Contact</p>
          <h2
            className="font-display uppercase leading-[0.9] text-white mb-8"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
            data-testid="contact-headline"
          >
            Talk to a<br />
            <span className="text-[#FF5722]">human.</span>
          </h2>
          <p className="font-body text-white/70 max-w-sm mb-10">
            Got a weird idea? Need a sanity check on a print? Send us a message —
            we read every one.
          </p>

          <ul className="space-y-5 border-t border-[#2A2A2A] pt-6">
            <li className="flex items-start gap-4">
              <Mail size={18} className="text-[#FF5722] mt-1" />
              <div>
                <p className="font-mono-ui text-[10px] tracking-[0.25em] text-white/50 uppercase mb-1">EMAIL</p>
                <a href="mailto:info@forgestate3d.com" className="font-mono-ui text-sm text-white hover:text-[#FF5722]">
                  info@forgestate3d.com
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <MapPin size={18} className="text-[#FF5722] mt-1" />
              <div>
                <p className="font-mono-ui text-[10px] tracking-[0.25em] text-white/50 uppercase mb-1">WORKSHOP</p>
                <p className="font-mono-ui text-sm text-white">
                  Based in Akron, OH
                </p>
              </div>
            </li>
          </ul>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-7 bg-[#0F0F0F] border border-[#2A2A2A] p-6 md:p-10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Name *</label>
              <input
                className="toad-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Your name"
                data-testid="contact-input-name"
                required
              />
            </div>
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Email *</label>
              <input
                type="email"
                className="toad-input"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@domain.com"
                data-testid="contact-input-email"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Subject</label>
              <input
                className="toad-input"
                value={form.subject}
                onChange={(e) => set("subject", e.target.value)}
                placeholder="What's this about?"
                data-testid="contact-input-subject"
              />
            </div>
            <div className="md:col-span-2">
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Message *</label>
              <textarea
                className="toad-input"
                rows={5}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Tell us what you're working on..."
                data-testid="contact-input-message"
                required
              />
            </div>
          </div>
          <div className="mt-8 flex justify-end border-t border-[#2A2A2A] pt-6">
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-3 bg-[#FF5722] hover:bg-white hover:text-black text-white font-mono-ui text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 transition-colors disabled:opacity-50"
              data-testid="contact-submit"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Message <ArrowRight size={16} /></>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
